import React from 'react'

import type { DatePickerProps } from 'react-datepicker'
import { useController } from 'react-hook-form'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import styled, { css } from 'styled-components'

import type { Locale } from '@libs/typescript-utils'

import { formatDate } from '@helpers'

import { BUTTON_CSS_VARIABLES, ButtonWithIcon } from '../Buttons'
import { CustomDatePicker } from '../CustomDatePicker'
import { InputLabel } from '../InputElements'
import { getInputBorderColor } from '../InputElements/helpers'
import { Popover } from '../Popover'
import { CalendarIcon } from '../svgIcons'
import { FormControl } from './FormControl'
import { FormHelperText } from './FormHelperText'

import { globalMessages } from '@messages'

const DatePickerRoot = styled.div`
  display: flex;
`

const StyledCalendarIcon = styled(CalendarIcon)`
  color: ${({ theme }) => theme.colors.common.oceanBlue};
`

const PlaceholderSpan = styled.span`
  color: ${({ theme }) => theme.colors.field.value};
`

const StyledButtonWithIcon = styled(ButtonWithIcon)<{ $error: boolean; $open: boolean }>`
  ${({ $error, $open, theme }) => {
    const borderColor = $open ? theme.colors.common.outlineBlue : getInputBorderColor({ $error, theme })

    return css`
      ${BUTTON_CSS_VARIABLES.backgroundColor}: ${theme.colors.field.background};
      ${BUTTON_CSS_VARIABLES.borderColor}: ${borderColor};
      ${BUTTON_CSS_VARIABLES.hoverBackgroundColor}: ${theme.colors.field.background};
      ${BUTTON_CSS_VARIABLES.hoverBorderColor}: ${borderColor};
      ${BUTTON_CSS_VARIABLES.activeBackgroundColor}: ${theme.colors.field.background};
      ${BUTTON_CSS_VARIABLES.focusBorderColor}: transparent;
      ${BUTTON_CSS_VARIABLES.disabledBackgroundColor}: ${theme.colors.field.disabledBackground};
      ${BUTTON_CSS_VARIABLES.disabledBorderColor}: ${theme.colors.field.disabledBorder};
      ${BUTTON_CSS_VARIABLES.disabledColor}: ${theme.colors.field.disabledValue};
    `
  }}

  padding: 6px 10px;
  gap: 8px;
  height: 36px;
  justify-content: flex-start;
  align-items: center;
  font-weight: 400;
  font-size: 1rem;

  &:not(:disabled):focus-visible:not(:active) {
    border-color: ${({ theme }) => theme.colors.common.outlineBlue};
  }

  &:not(:disabled):active {
    border-color: var(${BUTTON_CSS_VARIABLES.hoverBorderColor});
  }

  :disabled {
    svg {
      color: ${({ theme }) => theme.colors.field.disabledPlaceholder};
    }

    ${PlaceholderSpan} {
      color: ${({ theme }) => theme.colors.field.disabledPlaceholder};
    }
  }
`

type ReactHookFormDatePickerFieldProps = Pick<
  DatePickerProps,
  'showMonthYearPicker' | 'showFullMonthYearPicker' | 'showFourColumnMonthYearPicker'
> & {
  disabled?: boolean
  formats?: Record<Locale, string>
  label: React.ReactNode
  locale: Locale
  name: string
  onClose?: VoidFunction
  required?: boolean
  hideMonthSelect?: boolean
}

function PureReactHookFormDatePickerField({
  disabled,
  formats,
  label,
  locale,
  name,
  onClose,
  required = false,
  hideMonthSelect,
  showMonthYearPicker,
  showFullMonthYearPicker,
  showFourColumnMonthYearPicker,
}: ReactHookFormDatePickerFieldProps) {
  const [isOpen, setOpen] = React.useState(false)

  const {
    field: { onChange, value },
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ name })

  const inputId = `${name}-label`

  function onOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)

    if (!nextOpen) {
      onClose?.()
    }
  }

  function onDateChangeHandler(value: Nullable<Date>) {
    onChange(value ? formatDate(value) : null)
    onOpenChange(false)
  }

  return (
    <FormControl error={!!error}>
      <InputLabel htmlFor={inputId} error={!!error} required={required}>
        {label}
      </InputLabel>
      <Popover
        open={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom-start"
        trigger={
          <StyledButtonWithIcon
            $error={!!error}
            $open={isOpen}
            icon={<StyledCalendarIcon size={18} />}
            disabled={disabled || isSubmitting}
          >
            {!value ? (
              <PlaceholderSpan>
                <FormattedMessage {...globalMessages.dateFieldPlaceholder} />
              </PlaceholderSpan>
            ) : formats ? (
              formatDate(value, formats[locale])
            ) : (
              <FormattedDate value={value} />
            )}
          </StyledButtonWithIcon>
        }
      >
        <DatePickerRoot>
          <CustomDatePicker
            id={inputId}
            selected={value ? new Date(value) : undefined}
            onChange={onDateChangeHandler}
            locale={locale}
            inline
            fixedHeight
            showMonthYearPicker={showMonthYearPicker}
            showFullMonthYearPicker={showFullMonthYearPicker}
            showFourColumnMonthYearPicker={showFourColumnMonthYearPicker}
            hideMonthSelect={hideMonthSelect}
          />
        </DatePickerRoot>
      </Popover>
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  )
}

export const ReactHookFormDatePickerField = connect((state: Store) => ({
  locale: state.locale.language,
}))(PureReactHookFormDatePickerField)

ReactHookFormDatePickerField.displayName = 'ReactHookFormDatePickerField'
